/* section-cart.js — <cart-delivery>
   Saves the chosen delivery date to the cart as an attribute the moment the
   <select> changes, so it persists without waiting for the checkout submit.
   The select is also a real form field (name="attributes[Delivery Date]"),
   so the value still saves on submit if JS is unavailable. */
if (!customElements.get("cart-delivery")) {
  customElements.define(
    "cart-delivery",
    class CartDelivery extends HTMLElement {
      constructor() {
        super();
        this._onChange = this._onChange.bind(this);
        this._onDeliveryTimeChanged = this._onDeliveryTimeChanged.bind(this);
        this._onSubmit = this._onSubmit.bind(this);
      }

      connectedCallback() {
        this.select = this.querySelector("select");
        this._initialOptionsMarkup = this.select?.innerHTML || "";
        this.form = this.closest("form");
        this._deliveryCutoffChangePending = false;
        this.select?.addEventListener("change", this._onChange);
        this.form?.addEventListener("submit", this._onSubmit);
        document.addEventListener("delivery:time-changed", this._onDeliveryTimeChanged);
        this._applyDeliveryCutoff();
        this._deliveryCutoffTimer = window.setInterval(() => this._applyDeliveryCutoff(), 60 * 1000);

        // The date on the cart is no longer offered — a merchant skipped it
        // after it was chosen. The select has already fallen back to the first
        // available date; write that back so the cart can't carry a skipped
        // date to checkout just because nobody touched the dropdown.
        if (this.hasAttribute("data-date-unavailable") && this.select?.value) {
          this._onChange();
        }
      }

      disconnectedCallback() {
        this.select?.removeEventListener("change", this._onChange);
        this.form?.removeEventListener("submit", this._onSubmit);
        document.removeEventListener("delivery:time-changed", this._onDeliveryTimeChanged);
        window.clearInterval(this._deliveryCutoffTimer);
      }

      _applyDeliveryCutoff({ persist = true } = {}) {
        const cutoff = window.allplants && window.allplants.deliveryCutoff;
        if (!cutoff || !this.select) return;

        if (!cutoff.isPassed(this.dataset.deliveryTimezone)) {
          this._deliveryCutoffChangePending = false;
          this._deliveryCutoffReplacementLabel = "";
          if (this.select.innerHTML !== this._initialOptionsMarkup) {
            this.select.innerHTML = this._initialOptionsMarkup;
          }
          return;
        }

        const monday = cutoff.upcomingMondayIso(this.dataset.deliveryTimezone);
        const option = Array.from(this.select.options).find((item) => item.value === monday);
        if (!option) return;

        const wasSelected = option.selected;
        const replacement = cutoff.nextAvailableAfterCutoff(
          this.dataset.deliveryTimezone,
          Array.from(this.select.options).map((item) => `${item.value}::${item.textContent.trim()}`),
        );
        option.remove();
        this._deliveryCutoffChangePending = wasSelected;
        if (wasSelected) {
          const replacementOption =
            Array.from(this.select.options).find((item) => item.value === replacement?.iso) ||
            this.select.options[0];
          if (replacementOption) {
            this.select.value = replacementOption.value;
            this._deliveryCutoffReplacementLabel = replacementOption.textContent.trim();
          } else {
            this._deliveryCutoffReplacementLabel = replacement?.label || "the next available date";
          }
        } else {
          this._deliveryCutoffReplacementLabel = "";
        }
        if (persist && (wasSelected || this.hasAttribute("data-date-unavailable"))) {
          this._onChange();
        }
      }

      _onDeliveryTimeChanged() {
        // Console simulation is deliberately browser-local; do not write a
        // simulated delivery date to the Shopify cart.
        this._applyDeliveryCutoff({ persist: false });
      }

      _onSubmit(event) {
        // Native form submission carries the current select value, so refresh
        // it synchronously without an extra AJAX write before checkout.
        this._applyDeliveryCutoff({ persist: false });
        if (!this._deliveryCutoffChangePending) return;

        this._deliveryCutoffChangePending = false;
        event.preventDefault();
        window.alert(
          `Delivery cut-off passed. Date changed to ${this._deliveryCutoffReplacementLabel || "the next available date"}.`,
        );
      }

      _onChange() {
        const value = this.select.value;
        if (window.allplants && window.allplants.cart) {
          window.allplants.cart.updateAttributes({ "Delivery Date": value });
        }
      }
    },
  );
}
